import { REMOVED_QUOTE_FIELD_KEYS } from '../mock/productCatalog';
import type { Quote, QuoteFieldDefinitionRecord, QuoteItem } from './types';
import type { UnifiedFieldDescriptor } from './unifiedFieldRegistry';
import {
  createSystemFieldRegistry,
  createUnifiedFieldRegistry,
  FIXED_QUOTE_HEADER_FIELD_KEYS,
} from './unifiedFieldRegistry';

/** No unapproved pricing placeholders remain in the current registry. */
export const QUOTE_FIELD_SAFETY_KEYS: readonly string[] = [];

const safetyKeySet = new Set<string>(QUOTE_FIELD_SAFETY_KEYS);
const removedKeySet = new Set<string>(REMOVED_QUOTE_FIELD_KEYS);
const fixedQuoteHeaderKeySet = new Set<string>(FIXED_QUOTE_HEADER_FIELD_KEYS);

const PLAN_CONTEXT_KEYS = new Set<string>([
  'plan_name',
  'plan_image',
  'scheme_discount',
  'scheme_product_price',
  'scheme_product_subtotal',
]);

const PACKAGE_CONTEXT_KEYS = new Set<string>([
  'package_name',
  'package_image',
  'package_light_image',
  'package_product_links',
  'package_relation_quantity',
  'package_item_price',
  'package_item_monthly_subtotal',
  'package_discount_quote_tax13',
  'package_discounted_unit_price',
  'package_discounted_subtotal',
]);

const DEVICE_CONTEXT_KEYS = new Set<string>([
  'jky_code',
  'retail_price_tax13',
  'rental_price',
  'tax_inclusive_plan_price',
  'device_unit_price',
  'device_untaxed_plan_price',
  'device_compensation_price',
  'rental_subtotal',
  'unit_price_subtotal',
  'purchase_final_price_tax13',
  'purchase_final_price_ex_tax13',
  'dynamic_cost',
  'dynamic_cost_subtotal',
  'device_unit_price',
  'device_package_item_rental_price',
  'device_untaxed_plan_price',
  'device_image',
  'device_name',
  'device_sku',
  'warehouse_stock',
  'replacement_device',
  'device_category',
  'device_attribute',
  'procurement_attribute',
  'device_weight',
]);

export type QuoteFieldAvailability =
  | 'available'
  | 'unavailable'
  | 'not_applicable';

function availableRegistryKey(key: string, known: boolean): boolean {
  return (
    known &&
    !safetyKeySet.has(key) &&
    !removedKeySet.has(key) &&
    !fixedQuoteHeaderKeySet.has(key)
  );
}

export function isQuoteFieldAvailable(
  key: string,
  definitions?: readonly QuoteFieldDefinitionRecord[],
): boolean {
  let known = false;
  try {
    known = definitions
      ? createUnifiedFieldRegistry(definitions).some(
          (descriptor) => descriptor.definition.key === key,
        )
      : createSystemFieldRegistry().some(
          (descriptor) => descriptor.definition.key === key,
        ) || /^field\.custom\.new_[1-9]\d*$/.test(key);
  } catch {
    return false;
  }
  return availableRegistryKey(key, known);
}

/**
 * This is deliberately an availability guard, not a price resolver. It only
 * distinguishes known-inapplicable sources from fields whose contract is not
 * yet connected. It must not infer a formula or manufacture a fallback value.
 */
export function quoteFieldAvailabilityForItem(
  key: string,
  item: QuoteItem,
  definitions?: readonly QuoteFieldDefinitionRecord[],
): QuoteFieldAvailability {
  if (removedKeySet.has(key)) return 'unavailable';
  let descriptor: UnifiedFieldDescriptor | undefined;
  if (definitions) {
    try {
      descriptor = createUnifiedFieldRegistry(definitions).find(
        (candidate) => candidate.definition.key === key,
      );
    } catch {
      return 'unavailable';
    }
  }
  if (descriptor?.definition.governance.kind === 'custom') {
    if (descriptor.owner === 'quote-line') return 'available';
    if (item.sourceType === 'temporary') return 'not_applicable';
    if (descriptor.owner === 'device')
      return item.deviceId ? 'available' : 'not_applicable';
    if (descriptor.owner === 'package')
      return item.packageId ? 'available' : 'not_applicable';
    if (descriptor.owner === 'plan')
      return item.planId ? 'available' : 'not_applicable';
    // Customer custom fields are projected from the quote header, not a line.
    if (descriptor.owner === 'customer') return 'not_applicable';
    return 'unavailable';
  }
  if (
    item.sourceType === 'temporary' &&
    ['device_image', 'device_name', 'device_sku'].includes(key)
  ) {
    return 'available';
  }
  if (
    DEVICE_CONTEXT_KEYS.has(key) &&
    !['device', 'plan', 'package', 'receipt'].includes(item.sourceType)
  ) {
    return 'not_applicable';
  }
  if (PLAN_CONTEXT_KEYS.has(key) && !item.planId) {
    return 'not_applicable';
  }
  if (
    PACKAGE_CONTEXT_KEYS.has(key) &&
    !item.packageId &&
    !(key === 'package_discounted_subtotal' && item.sourceType === 'device')
  ) {
    return 'not_applicable';
  }
  return (
    definitions
      ? availableRegistryKey(key, descriptor !== undefined)
      : isQuoteFieldAvailable(key)
  )
    ? 'available'
    : 'unavailable';
}

/** Quote-level availability is the binding gate before a selected field can
 * become a column. It checks the concrete source object, never its label. */
export function quoteFieldAvailabilityForQuote(
  key: string,
  quote: Quote,
  definitions: readonly QuoteFieldDefinitionRecord[],
): QuoteFieldAvailability {
  let descriptor: UnifiedFieldDescriptor | undefined;
  try {
    descriptor = createUnifiedFieldRegistry(definitions).find(
      (candidate) => candidate.definition.key === key,
    );
  } catch {
    return 'unavailable';
  }
  if (!availableRegistryKey(key, descriptor !== undefined))
    return 'unavailable';
  if (descriptor?.definition.governance.kind !== 'custom') return 'available';
  if (descriptor.owner === 'quote-line') return 'available';
  if (descriptor.owner === 'customer')
    return quote.customer.customerId ? 'available' : 'not_applicable';
  const applicable = quote.items.some(
    (item) =>
      quoteFieldAvailabilityForItem(key, item, definitions) === 'available',
  );
  return applicable ? 'available' : 'not_applicable';
}

export function quoteFieldAvailabilityLabel(
  availability: Exclude<QuoteFieldAvailability, 'available'>,
): string {
  return availability === 'not_applicable' ? '不适用' : '未接入';
}
